// src/pages/autopilot/ICPValidation.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useDispatch, useSelector } from 'react-redux';
import {
  setValidatedICP,
  setLoading,
  setError,
  setGeneratedICP,
  goToPreviousStep
} from '../../store/slices/autoPilotSlice';
import { validateICP } from '../../services/autoPilotService';
import ICPDisplay from '../../components/autopilot/ICPDisplay';

function ICPValidation({ onNext, onBack }) {
  const dispatch = useDispatch();
  const { generatedICP, loading, error } = useSelector((state) => state.autoPilot);
  const [localICP, setLocalICP] = useState(generatedICP || null);

  useEffect(() => {
    if (generatedICP) {
      setLocalICP(generatedICP);
    }
  }, [generatedICP]);

  const handleUpdate = (updatedICP) => {
    setLocalICP(updatedICP);
    dispatch(setGeneratedICP(updatedICP));
  };

  const handleValidate = async () => {
    if (!localICP) {
      dispatch(setError('No ICP data available'));
      return;
    }

    dispatch(setLoading(true));
    dispatch(setError(null));

    try {
      const response = await validateICP(localICP, {});
      if (response.status === 'success') {
        dispatch(setValidatedICP(response.data || localICP));
        if (onNext) {
          onNext();
        }
      } else {
        dispatch(setError(response.message || 'Failed to validate ICP. Please try again.'));
      }
    } catch (err) {
      // If validation fails, proceed with local ICP
      dispatch(setValidatedICP(localICP));
      if (onNext) {
        onNext();
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      dispatch(goToPreviousStep());
    }
  };

  if (!localICP) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3, textAlign: 'center' }}>
        <Alert severity="warning">
          No ICP data found. Please go back and generate an ICP first.
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
        Review and Validate ICP
      </Typography>
      <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
        Review the generated Ideal Customer Profile below. You can edit any section before proceeding. Once validated, we'll generate search queries based on this ICP.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => dispatch(setError(null))}>
          {error}
        </Alert>
      )}

      <ICPDisplay icpData={localICP} editable={true} onUpdate={handleUpdate} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          disabled={loading}
          sx={{
            borderColor: '#666',
            color: '#666',
            '&:hover': { borderColor: '#333', bgcolor: '#f5f5f5' }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleValidate}
          disabled={loading}
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          sx={{
            bgcolor: '#1BE885',
            color: '#000',
            fontWeight: 600,
            '&:hover': { bgcolor: '#20ec84' },
            '&:disabled': { bgcolor: '#ccc' }
          }}
        >
          {loading ? 'Validating...' : 'Validate & Continue'}
        </Button>
      </Box>
    </Box>
  );
}

export default ICPValidation;
